<template>
  <b-modal id="restriktionenKalendertagModal" centered scrollable hide-footer>
    <template #modal-header="{ close }">
      <div class="restriktionen-kalendertag-modal__header">
        <div class="restriktionen-kalendertag-modal__heading">
          <span class="restriktionen-kalendertag-modal__title">{{ title }}</span>
          <span class="restriktionen-kalendertag-modal__subtitle">{{ subtitle }}</span>
        </div>
        <div class="restriktionen-kalendertag-modal__header-actions">
          <b-button
            class="icon-button d-inline-flex justify-content-center align-items-center restriktionen-kalendertag-modal__add"
            v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
            title="Sperrzeit hinzufügen"
            @click="$emit('edit-day')"
          >
            <CalendarPlus :size="16" />
          </b-button>
          <span class="restriktionen-kalendertag-modal__close" @click="close()">
            <X :size="20" />
          </span>
        </div>
      </div>
    </template>

    <!-- Karten der Sperrzeiten des Tages, Reihenfolge nach Priorität -->
    <div class="restriktionen-kalendertag-modal__body">
      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="restriktionen-kalendertag-modal__card"
        :style="{
          backgroundColor: typeConfig(entry.type).bgColor,
          borderColor: typeConfig(entry.type).borderColor,
        }"
      >
        <div class="restriktionen-kalendertag-modal__card-top">
          <span
            class="restriktionen-kalendertag-modal__badge"
            :style="{
              backgroundColor: typeConfig(entry.type).bgColor,
              color: typeConfig(entry.type).textColor,
            }"
          >
            <component :is="typeConfig(entry.type).icon" :size="14" />
            {{ typeConfig(entry.type).modalLabel }}
          </span>

          <div class="restriktionen-kalendertag-modal__card-actions">
            <b-button
              class="icon-button d-inline-flex justify-content-center align-items-center"
              v-b-tooltip.hover="{ customClass: 'tooltip-opacity' }"
              title="Bearbeiten"
              @click="$emit('edit', entry)"
            >
              <PencilLine :size="14" />
            </b-button>
            <b-button
              v-if="istWiederkehrend(entry)"
              class="icon-button d-inline-flex justify-content-center align-items-center"
              v-b-tooltip.hover="{ customClass: 'tooltip-opacity' }"
              title="Tag löschen"
              @click="$emit('delete-tag', entry)"
            >
              <CalendarOff :size="14" />
            </b-button>
            <b-button
              class="icon-button d-inline-flex justify-content-center align-items-center restriktionen-kalendertag-modal__delete"
              v-b-tooltip.hover="{ customClass: 'tooltip-opacity' }"
              title="Sperrzeit löschen"
              @click="$emit('delete-serie', entry)"
            >
              <Trash2 :size="14" />
            </b-button>
          </div>
        </div>

        <div class="restriktionen-kalendertag-modal__card-title">
          <template v-if="wiederholungLabel(entry)">{{ wiederholungLabel(entry) }} ·</template>
          {{ entry.grund }}
        </div>

        <div v-if="entry.laender && entry.laender.length" class="restriktionen-kalendertag-modal__zuordnung">
          <Globe :size="14" />
          <span>{{ entry.laender.join(', ') }}</span>
        </div>
        <div
          v-if="entry.reisekuerzel && entry.reisekuerzel.length"
          class="restriktionen-kalendertag-modal__zuordnung"
        >
          <Plane :size="14" />
          <span>{{ entry.reisekuerzel.join(', ') }}</span>
        </div>

        <div v-if="zeitraum(entry)" class="restriktionen-kalendertag-modal__zeitraum">
          {{ zeitraum(entry) }}
        </div>

        <div v-if="entry.kommentar" class="restriktionen-kalendertag-modal__kommentar">
          {{ entry.kommentar }}
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { PencilLine, Trash2, X, Globe, Plane, CalendarPlus } from 'lucide-vue';
import { getWiederholungLabel } from './restriktionen.constants.js';
import { formatWochentagDatum, formatZeitraumText } from './restriktionen-calendar.helper.js';
import restriktionenTypMixin from './restriktionen-typ.mixin.js';

// Tages-Detail-Modal der Restriktionen
export default {
  name: 'RestriktionenKalendertagModalComponent',
  mixins: [restriktionenTypMixin],
  components: {
    PencilLine,
    Trash2,
    X,
    Globe,
    Plane,
    CalendarPlus,
  },
  props: {
    date: {
      type: [Object, String, Date],
      default: null,
    },
    entries: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    title() {
      return this.date ? formatWochentagDatum(this.date, { lang: true }) : '';
    },
    subtitle() {
      const anzahl = this.entries.length;
      return `${anzahl} ${anzahl === 1 ? 'Sperrzeit' : 'Sperrzeiten'} an diesem Tag`;
    },
  },
  methods: {
    wiederholungLabel(entry) {
      return getWiederholungLabel(entry.wiederholungstyp);
    },
    istWiederkehrend(entry) {
      return !!getWiederholungLabel(entry.wiederholungstyp);
    },
    // Einmalige Sperrzeiten erhalten keine Zeitzeile, ihr Tag steht bereits in der Überschrift
    zeitraum(entry) {
      return entry.wiederholungstyp === 'Single' ? null : formatZeitraumText(entry);
    },
  },
};
</script>

<style scoped>
.restriktionen-kalendertag-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.restriktionen-kalendertag-modal__heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.restriktionen-kalendertag-modal__title {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: var(--text);
}

.restriktionen-kalendertag-modal__subtitle {
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  color: var(--text-muted);
}

.restriktionen-kalendertag-modal__header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.restriktionen-kalendertag-modal__add.btn.icon-button {
  width: 32px;
  min-width: 32px;
  height: 32px;
  padding: 0;
}

.restriktionen-kalendertag-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-muted);
}

.restriktionen-kalendertag-modal__close:hover {
  color: var(--text-body);
}

.restriktionen-kalendertag-modal__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.restriktionen-kalendertag-modal__card {
  padding: 16px;
  border: 1px solid;
  border-radius: var(--radius-md);
}

.restriktionen-kalendertag-modal__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.restriktionen-kalendertag-modal__badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px;
  border-radius: var(--radius-sm);
  font-size: 11px;
  line-height: 13px;
  font-weight: 700;
}

.restriktionen-kalendertag-modal__card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.restriktionen-kalendertag-modal__card-actions .icon-button {
  width: 38px;
  min-width: 38px;
  padding: 0;
}

.restriktionen-kalendertag-modal__delete {
  color: var(--danger);
}

.restriktionen-kalendertag-modal__card-title {
  margin-top: 14px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  color: var(--text-body);
}

.restriktionen-kalendertag-modal__zuordnung {
  font-size: 11px;
  line-height: 13px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: var(--text-body);
}

.restriktionen-kalendertag-modal__zeitraum {
  margin-top: 8px;
  font-size: 11px;
  line-height: 13px;
  color: var(--text-body);
}

.restriktionen-kalendertag-modal__kommentar {
  margin-top: 8px;
  font-size: 12px;
  line-height: 14px;
  color: var(--text-body);
}
</style>
